/** Sales document kinds (API route segments of docs/contracts/sales.md) and their permission keys. */
export const salesKinds = [
  "quotations",
  "orders",
  "pick-lists",
  "deliveries",
  "invoices",
  "returns",
  "return-requests",
  "price-credits",
] as const;

export type SalesKind = (typeof salesKinds)[number];

const feature: Record<SalesKind, string> = {
  quotations: "sale_quotation",
  orders: "sale_order",
  "pick-lists": "sale_pick_and_pack",
  deliveries: "goods_issued",
  invoices: "sale_accounting",
  returns: "goods_return",
  "return-requests": "sale_return_request",
  "price-credits": "tax_credit_note",
};

/** Translation key stem of a kind (`sales:kinds.<key>.*`) and of its navigation entry. */
export const kindKey: Record<SalesKind, string> = {
  quotations: "quotation",
  orders: "order",
  "pick-lists": "pickList",
  deliveries: "delivery",
  invoices: "invoice",
  returns: "return",
  "return-requests": "returnRequest",
  "price-credits": "priceCredit",
};

/** Kind codes of the API (`kind` of a document view). */
export const kindCode: Record<SalesKind, string> = {
  quotations: "quotation",
  orders: "order",
  "pick-lists": "pickList",
  deliveries: "delivery",
  invoices: "invoice",
  returns: "return",
  "return-requests": "returnRequest",
  "price-credits": "priceCredit",
};

/** Kinds that are released (no journal), the others are posted. */
export const releaseKinds: readonly SalesKind[] = ["quotations", "orders", "pick-lists", "return-requests"];

/** Kinds whose lines may be copied in (SP1, SP4), in the order offered. */
export const sourceKinds: Record<SalesKind, readonly SalesKind[]> = {
  quotations: [],
  orders: ["quotations"],
  "pick-lists": ["orders"],
  deliveries: ["orders", "pick-lists", "quotations"],
  invoices: ["deliveries", "orders", "quotations"],
  returns: ["invoices", "return-requests"],
  "return-requests": ["invoices"],
  "price-credits": ["invoices"],
};

export type SalesAction =
  "view" | "create" | "update" | "delete" | "approve" | "post" | "reverse" | "cancel" | "print" | "close";

/** Catalog key of an action on a kind; release kinds use their legacy "post" key for release. */
export function kindPermission(kind: SalesKind, action: SalesAction): string {
  return `sales.${feature[kind]}.${action}`;
}

export function isSalesKind(value: string): value is SalesKind {
  return (salesKinds as readonly string[]).includes(value);
}

export function kindOfCode(code: string): SalesKind | null {
  return salesKinds.find((kind) => kindCode[kind] === code) ?? null;
}

/** Route paths of the list and object pages of each kind (typed router: literal paths). */
export const listPaths = {
  quotations: "/sales/quotations",
  orders: "/sales/orders",
  "pick-lists": "/sales/pick-lists",
  deliveries: "/sales/deliveries",
  invoices: "/sales/invoices",
  returns: "/sales/returns",
  "return-requests": "/sales/return-requests",
  "price-credits": "/sales/price-credits",
} as const satisfies Record<SalesKind, string>;

export const objectPaths = {
  quotations: "/sales/quotations/$documentId",
  orders: "/sales/orders/$documentId",
  "pick-lists": "/sales/pick-lists/$documentId",
  deliveries: "/sales/deliveries/$documentId",
  invoices: "/sales/invoices/$documentId",
  returns: "/sales/returns/$documentId",
  "return-requests": "/sales/return-requests/$documentId",
  "price-credits": "/sales/price-credits/$documentId",
} as const satisfies Record<SalesKind, string>;

/** Report keys of the sales and receivables reports. */
export const reportKeys = ["aging", "credit-exposure", "sales-analysis", "open-orders"] as const;
export type SalesReportKey = (typeof reportKeys)[number];

/** Kinds with a document discount, freight and service charge; returns, return requests, price credits and pick lists follow their source. */
export function takesHeaderCharges(kind: SalesKind): boolean {
  return kind !== "returns" && kind !== "return-requests" && kind !== "price-credits" && kind !== "pick-lists";
}
